import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ProgressiveTypeaheadDirective } from '../../../directives/progressive-typeahead.directive';
import { MatTableModule } from '@angular/material/table';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { CsrCountryCapitalizationEntryDto } from '../../../models/api/csrCountryCapitalizationDto';
import { CountryFlat } from '../../../models/country';
import { LutService } from '../../../services/lut.service';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-csr-country-capitalization-list',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    ProgressiveTypeaheadDirective,
    MatTableModule,
    NgxSpinnerModule
  ],
  templateUrl: './csr-country-capitalization-list.component.html',
  styleUrl: './csr-country-capitalization-list.component.scss'
})
export class CsrCountryCapitalizationListComponent implements OnInit {
  years: number[] = [];
  selectedYear!: number;
  isCarriedForward = false;
  hasYearData = false;
  entries = signal<CsrCountryCapitalizationEntryDto[]>([]);
  displayedColumns = ['countryName', 'isLarge', 'isMedium', 'isSmall'];

  private visibleCountries: CountryFlat[] = [];

  constructor(
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {
    const startYear = Number(Helpers.defaultMeasurementYear());
    for (let year = startYear; year >= 2007; year--) {
      this.years.push(year);
    }
    this.selectedYear = startYear;
  }

  ngOnInit(): void {
    this.spinnerService.show();
    this.lutService.getVisibleCountries().subscribe({
      next: (countries) => {
        this.visibleCountries = [...countries].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
        this.loadEntries();
      },
      error: (error) => {
        console.error('Error loading countries:', error);
        this.spinnerService.hide();
        this.toast.error(`<strong>Failed to load countries:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onYearChange(): void {
    this.loadEntries();
  }

  loadEntries(): void {
    this.spinnerService.show();
    this.lutService.getCsrCountryCapitalizations(this.selectedYear).subscribe({
      next: (data) => {
        this.isCarriedForward = data.isCarriedForward;
        this.hasYearData = data.entries.length > 0;
        this.entries.set(this.mergeEntries(data.entries));
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading ESG country capitalizations:', error);
        this.spinnerService.hide();
        this.toast.error(`<strong>Failed to load ESG Country Capitalizations:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  private mergeEntries(apiEntries: CsrCountryCapitalizationEntryDto[]): CsrCountryCapitalizationEntryDto[] {
    const byCountryId = new Map(apiEntries.map(e => [e.countryId, e]));
    return this.visibleCountries.map(country => {
      const existing = byCountryId.get(country.id);
      if (existing) {
        return { ...existing, countryName: country.name };
      }
      return {
        id: '',
        countryId: country.id,
        countryName: country.name,
        isLarge: false,
        isMedium: false,
        isSmall: false,
        createdDate: '',
        createdBy: '',
        updatedDate: '',
        updatedBy: ''
      };
    });
  }

  onEntryChange(entry: CsrCountryCapitalizationEntryDto, field: 'isLarge' | 'isMedium' | 'isSmall', value: boolean): void {
    this.entries.update(items => items.map(e => e.countryId === entry.countryId ? { ...e, [field]: value } : e));
  }

  onSave(): void {
    const request = {
      year: this.selectedYear,
      entries: this.entries().map(e => ({
        countryId: e.countryId,
        isLarge: e.isLarge,
        isMedium: e.isMedium,
        isSmall: e.isSmall
      }))
    };

    this.spinnerService.show();
    this.lutService.upsertCsrCountryCapitalizations(request).subscribe({
      next: (data) => {
        this.spinnerService.hide();
        this.toast.success('ESG country capitalizations saved successfully');
        this.isCarriedForward = data.isCarriedForward;
        this.hasYearData = data.entries.length > 0;
        this.entries.set(this.mergeEntries(data.entries));
      },
      error: (error) => {
        console.error('Error saving ESG country capitalizations:', error);
        this.spinnerService.hide();
        this.toast.error(`<strong>Failed to save ESG Country Capitalizations:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

}
